/** PIN-derived device session: 4-day TTL in localStorage. */

export const PIN_SESSION_TTL_MS = 4 * 24 * 60 * 60 * 1000;
export const PIN_AUTH_AT_KEY = 'kuutio-pin-auth-at';
/** Set once the server has accepted this device's PIN session (checkPinSession). */
export const PIN_VERIFIED_KEY = 'kuutio-pin-verified';

const PIN_SESSION_KEYS = [
  'kuutio-instance',
  'kuutio-participant-instance',
  'kuutio-participant-auth',
  'kuutio-admin-auth',
  'kuutio-screen-admin',
  'kuutio-screen-code',
  'kuutio-device-locked',
  PIN_AUTH_AT_KEY,
  PIN_VERIFIED_KEY,
];

function storage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function storageGet(key) {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    storage()?.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

function storageRemove(key) {
  try {
    storage()?.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

export function markPinSession(now = Date.now()) {
  storageSet(PIN_AUTH_AT_KEY, String(now));
}

export function isPinSessionValid(now = Date.now()) {
  const raw = storageGet(PIN_AUTH_AT_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  return now - at < PIN_SESSION_TTL_MS;
}

export function clearPinSession() {
  PIN_SESSION_KEYS.forEach(storageRemove);
}

/**
 * Ask the server whether this device's PIN session is still alive.
 *
 * The timestamp above only says when the PIN was typed; the session itself is
 * the server's (an HttpOnly cookie), and it can end first. Returns:
 *
 *   'ok'      the server accepts the session
 *   'stale'   the server accepted it before and no longer does
 *   'unknown' no verdict: nothing to check, a network error or a server
 *             error, or a 401 on a device the server has never accepted
 *
 * That last case is a browser that drops the cookie (third-party cookies
 * blocked, #759). Such a device gets 401 right after a correct PIN, so sending
 * it back to the PIN form would loop; it carries on as before.
 */
export async function checkPinSession(verifyUrl, fetchImpl = globalThis.fetch) {
  if (!isPinSessionValid()) return 'unknown';
  let response;
  try {
    response = await fetchImpl(verifyUrl, { credentials: 'include' });
  } catch {
    return 'unknown';
  }
  if (response.ok) {
    storageSet(PIN_VERIFIED_KEY, 'true');
    return 'ok';
  }
  if (response.status === 401 && storageGet(PIN_VERIFIED_KEY) === 'true') return 'stale';
  return 'unknown';
}

/** Returns the stored instance id if the PIN session is still valid; otherwise clears and returns null. */
export function getValidStoredInstance(now = Date.now()) {
  if (!isPinSessionValid(now)) {
    clearPinSession();
    return null;
  }
  return storageGet('kuutio-instance') || storageGet('kuutio-participant-instance') || null;
}

/** The instance this device's PIN opens, or null. Reads only; isPinSessionValid() says whether it counts. */
export function getPinInstanceId() {
  return storageGet('kuutio-participant-instance') || storageGet('kuutio-instance') || null;
}

/** The PIN a link carries (`/?pin=…`, the admin's PIN menu), upper-cased; null without one. */
export function getLinkedPin(search = globalThis.location?.search ?? '') {
  const pin = new URLSearchParams(search).get('pin');
  return pin && pin.trim() ? pin.trim().toUpperCase() : null;
}

export const DENIED_REASONS = ['signin', 'instance', 'expired'];

/**
 * Why AuthGate sent this visitor to the front page (`/?denied=…`), or null:
 * 'signin' no session, 'instance' a session without access to the link's
 * workshop, 'expired' a PIN session the server no longer accepts.
 */
export function getDeniedReason(search = globalThis.location?.search ?? '') {
  const reason = new URLSearchParams(search).get('denied');
  return DENIED_REASONS.includes(reason) ? reason : null;
}
